import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { removeEmptyItems } from '../../common/util'
import {
  InputParametersHierarchyId,
  InputParametersHierarchyNodeByPresets,
  InputParametersHierarchyNodeTemplateReference,
  InputParametersHierarchyTemplateResolution,
} from '../../models/InputParametersHierarchy'
import { InputParameterHierarchyType } from '../../components/InputParameterHierarchy/model'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface CreateInputParametersHierarchyNodeWithInputParameters {
  hierarchyId: InputParametersHierarchyId
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputParameters: { [key: string]: any }
}

export interface CreateInputParametersHierarchyNodeWithTemplateReference {
  hierarchyId: InputParametersHierarchyId
  templateId: string
}

export type CreateInputParametersHierarchyNode = CreateInputParametersHierarchyNodeWithInputParameters | CreateInputParametersHierarchyNodeWithTemplateReference

export interface UpdateInputParametersHierarchyNode {
  active?: boolean
  templateId?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputParameters?: { [key: string]: any }
}

export interface InputParametersHierarchyContext {
  effective?: boolean
  level?: string
  defaultTypeId?: string
  userId?: string
  meetingTemplateId?: string
  emailTypeId?: string
  documentTypeId?: string
  contextType?: 'meeting_template' | 'email_type' | 'document_type'
}

export interface InputParametersHierarchyNode {
  id: string
  hierarchyId: InputParametersHierarchyId
  active: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputParameters: { [key: string]: any }
  templateId?: string
  isEnterpriseEnforced?: boolean
}

interface BaseNodeRequest extends BaseRequest {
  type: InputParameterHierarchyType
  typeId: string
}

interface ExistingNodeRequest extends BaseNodeRequest {
  nodeId: string
}

export interface GetEffectiveNodeRequest extends BaseNodeRequest {
  context: InputParametersHierarchyContext
}

export interface CreateNodeRequest extends BaseNodeRequest {
  node: CreateInputParametersHierarchyNode
}

export interface UpdateNodeRequest extends ExistingNodeRequest {
  node: UpdateInputParametersHierarchyNode
}

export interface GetInputParametersHierarchyRequest {
  accountId: string
  type: string
  typeIds: string[]
  effective?: boolean
  meetingTemplateId?: string
  emailTypeId?: string
  contextType?: 'meeting_template' | 'email_type'
  userId?: string
}

export interface InputParametersHierarchyDefault {
  type: InputParameterHierarchyType
  typeId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputParameters: { [key: string]: any }
  templateId: string
}

export interface GetTemplateReferencesByContextRequest extends BaseNodeRequest {
  context: InputParametersHierarchyContext
}

export const inputParametersHierarchyApi = createApi({
  reducerPath: 'inputParametersHierarchyApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['InputParametersHierarchy'],
  endpoints: (builder) => ({
    createNode: builder.mutation<BaseResponse<InputParametersHierarchyNode>, CreateNodeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/`,
        method: 'POST',
        body: removeEmptyItems(request.node),
      }),
      invalidatesTags: ['InputParametersHierarchy'],
    }),
    getNode: builder.query<BasePageResponse<InputParametersHierarchyNode>, ExistingNodeRequest>({
      query: (request) => `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/${request.nodeId}`,
    }),
    getEffectiveNode: builder.query<InputParametersHierarchyNode, GetEffectiveNodeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/`,
        params: request.context,
        validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
      }),
      providesTags: ['InputParametersHierarchy'],
      transformResponse(response: BaseResponse<InputParametersHierarchyNode>) {
        return response.data
      },
    }),
    updateNode: builder.mutation<BaseResponse<InputParametersHierarchyNode>, UpdateNodeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/${request.nodeId}`,
        method: 'PATCH',
        body: removeEmptyItems(request.node),
      }),
      invalidatesTags: ['InputParametersHierarchy'],
    }),
    deleteNode: builder.mutation<void, ExistingNodeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/${request.nodeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['InputParametersHierarchy'],
    }),
    getInputParametersHierarchyNodeByPresets: builder.query<InputParametersHierarchyNodeByPresets, GetInputParametersHierarchyRequest>({
      query: (request) => {
        const { accountId, type, typeIds, effective, meetingTemplateId, userId } = request
        return {
          url: `/accounts/${accountId}/input-parameters-hierarchy`,
          params: {
            type: type,
            aiFeaturePresetIds: typeIds.join(),
            ...(effective && { effective }),
            ...(meetingTemplateId && { meetingTemplateId }),
            ...(userId && { userId }),
          },
        }
      },
      transformResponse(response: BaseResponse<InputParametersHierarchyNodeByPresets>) {
        return response.data
      },
    }),
    getInputParametersHierarchyDefault: builder.query<InputParametersHierarchyDefault, BaseNodeRequest>({
      query: (request) => `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/default`,
      transformResponse(response: BaseResponse<InputParametersHierarchyDefault>) {
        return response.data
      },
    }),
    getTemplateReferencesByContext: builder.query<InputParametersHierarchyTemplateResolution, GetTemplateReferencesByContextRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/references`,
        params: request.context,
        validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
      }),
      transformResponse(response: BaseResponse<InputParametersHierarchyTemplateResolution>) {
        return response.data
      },
      providesTags: ['InputParametersHierarchy'],
    }),
    getTemplateReferencesForAllUsersByContext: builder.query<InputParametersHierarchyNodeTemplateReference[], GetTemplateReferencesByContextRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/${request.type}/${request.typeId}/input-parameters-hierarchy/references/all`,
        params: request.context,
        validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
      }),
      transformResponse(response: BaseResponse<InputParametersHierarchyNodeTemplateReference[]>) {
        return response.data
      },
      providesTags: ['InputParametersHierarchy'],
    }),
  }),
})

export const {
  useCreateNodeMutation,
  useGetEffectiveNodeQuery,
  useLazyGetEffectiveNodeQuery,
  useUpdateNodeMutation,
  useDeleteNodeMutation,
  useLazyGetInputParametersHierarchyNodeByPresetsQuery,
  useGetInputParametersHierarchyNodeByPresetsQuery,
  useGetInputParametersHierarchyDefaultQuery,
  useLazyGetInputParametersHierarchyDefaultQuery,
  useGetTemplateReferencesByContextQuery,
  useLazyGetTemplateReferencesByContextQuery,
  useGetTemplateReferencesForAllUsersByContextQuery,
  useLazyGetTemplateReferencesForAllUsersByContextQuery,
} = inputParametersHierarchyApi
